import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Dual-auth: httpOnly cookies (primary) + localStorage bearer token (fallback).
 * New logins get both — existing localStorage sessions still work.
 * Over time, as users log in after this rollout, their tokens migrate to cookies.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const getToken = () => localStorage.getItem('session_token');

  // Bearer header is still returned for back-compat (admin portal + some routes);
  // BUT cookies are now the primary transport.
  const authHeaders = useCallback(() => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const token = getToken();
      // Include cookies AND (if present) legacy bearer. Either will authenticate.
      const res = await fetch(`${API}/auth/me`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        // Sanitise BEFORE state dispatch. Defined later in this file but hoisted
        // via const reference at call time — works because login/register call
        // sanitiseUser which is a const declared above setUser usage in this
        // same closure. For checkAuth we inline the JSON.stringify trick to
        // avoid a forward reference (sanitiseUser hoists later in the file).
        let safe;
        try { safe = JSON.parse(JSON.stringify(data)); } catch { safe = null; }
        setUser(safe);
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz && tz !== data.timezone) {
          fetch(`${API}/auth/timezone`, {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
            body: JSON.stringify({ timezone: tz }),
          }).catch(() => {});
        }
      } else {
        if (res.status === 401) localStorage.removeItem('session_token');
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    document.body.style.transition = 'opacity 0.3s ease';
    document.body.style.opacity = '0';
    try {
      const token = getToken();
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    } catch {}
    localStorage.removeItem('session_token');
    setUser(null);
    setTimeout(() => { document.body.style.opacity = '1'; }, 100);
  };

  const syncTimezone = (token) => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) {
      fetch(`${API}/auth/timezone`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
        body: JSON.stringify({ timezone: tz }),
      }).catch(() => {});
    }
  };

  /**
   * Wrapped fetch that survives any 3rd-party fetch interceptor (PostHog, Sentry,
   * Cloudflare's challenge-platform, etc.) that may wrap window.fetch and throw
   * synchronously on non-cloneable Request bodies. If a non-DataCloneError-related
   * error is thrown by the wrapper (not a real network failure), we retry once
   * using the captured native fetch, ensuring sign-in cannot be permanently broken
   * by an analytics-side bug.
   */
  const NATIVE_FETCH = typeof window !== 'undefined' ? window.fetch.bind(window) : null;
  const safeFetch = async (url, opts) => {
    try {
      return await fetch(url, opts);
    } catch (err) {
      const msg = (err && (err.message || String(err))) || '';
      if (NATIVE_FETCH && /clone|postMessage|DataClone/i.test(msg)) {
        // 3rd-party wrapper threw on a clone attempt — retry with the original.
        try { return await NATIVE_FETCH(url, opts); } catch { /* fall through */ }
      }
      throw err;
    }
  };

  /**
   * Defensive payload sanitiser. Converts whatever the auth endpoint returned
   * into a plain JSON object containing ONLY primitives, plain objects, and
   * arrays — NEVER a Response, Request, Error, Stream, or class instance.
   */
  const sanitiseUser = (raw) => {
    if (!raw || typeof raw !== 'object') return null;
    try {
      // structuredClone-safe: serialize then parse strips functions, class
      // instances, Symbols, and any non-cloneable inner property.
      return JSON.parse(JSON.stringify(raw));
    } catch {
      return null;
    }
  };

  const login = async (email, password) => {
    const res = await safeFetch(`${API}/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    // Always parse the response body BEFORE any state update / dispatch.
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      // Throw a plain Error with a string message — never re-throw the Response.
      throw new Error(typeof data?.detail === 'string' ? data.detail : 'Login failed');
    }
    // Cookie is now set by the server. Store legacy bearer for in-progress migrations too.
    if (typeof data.session_token === 'string') localStorage.setItem('session_token', data.session_token);
    const safeUser = sanitiseUser(data.user);
    setUser(safeUser);
    syncTimezone(data.session_token);
    return safeUser;
  };

  const register = async (email, password, name, role, extras = {}) => {
    const res = await safeFetch(`${API}/auth/register`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name, role, ...extras }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(typeof data?.detail === 'string' ? data.detail : 'Registration failed');
    }
    if (typeof data.session_token === 'string') localStorage.setItem('session_token', data.session_token);
    const safeUser = sanitiseUser(data.user);
    setUser(safeUser);
    syncTimezone(data.session_token);
    return safeUser;
  };

  const updateUser = (data) => setUser(prev => ({ ...prev, ...data }));

  return (
    <AuthContext.Provider value={{ user, loading, checkAuth, logout, login, register, getToken, authHeaders, updateUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
